import React, {
  useCallback,
  useState,
  useRef,
  useEffect,
  useMemo,
} from "react";
import FullMediaPreview from "./FullMediaPreview";
import {
  useRegisterSingleActiveMedia,
  useSingleActiveMedia,
} from "../../hooks/useSingleActiveMedia";

const RenderMediaGroup = ({ media }) => {
  const { register, claimActive, releaseActive } = useSingleActiveMedia();
  const [showFullPreview, setShowFullPreview] = useState(false);
  const [fullPreviewIndex, setFullPreviewIndex] = useState(0);
  // Prepare all media array
  const allMedia = useMemo(
    () => [
      ...(media?.images?.map((url) => ({ type: "image", url })) || []),
      ...(media?.videos?.map((url) => ({ type: "video", url })) || []),
      ...(media?.documents?.map((url) => ({ type: "document", url })) || []),
    ],
    [media],
  );

  // Media navigation state
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);

  // Video state
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [videoKey, setVideoKey] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);

  const stopPlayback = useCallback(() => {
    if (videoRef.current && !videoRef.current.paused) {
      videoRef.current.pause();
    }
    setIsPlaying(false);
    setShowControls(true);
  }, []);

  useRegisterSingleActiveMedia(stopPlayback, register);

  const handleVideoPlay = useCallback(() => {
    setIsPlaying(true);
  }, []);

  const handleVideoPause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const handleVideoLoadedMetadata = useCallback((e) => {
    const video = e.currentTarget;
    if (Number.isFinite(video.duration)) {
      setDuration(video.duration);
    }
  }, []);

  const handleVideoTimeUpdate = useCallback((e) => {
    const video = e.currentTarget;
    setCurrentTime(video.currentTime);
    if (Number.isFinite(video.duration) && video.duration > 0) {
      setProgress((video.currentTime / video.duration) * 100);
    }
  }, []);

  const handleVideoEnded = useCallback(() => {
    setIsPlaying(false);
    setShowControls(true);
    releaseActive();
  }, [releaseActive]);

  // Reset video state when media changes
  useEffect(() => {
    if (allMedia[currentMediaIndex]?.type === "video") {
      setIsPlaying(false);
      setProgress(0);
      setCurrentTime(0);
      setDuration(0);
      setShowControls(true);

      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        videoRef.current.pause();
      }
    }
  }, [currentMediaIndex, allMedia]);

  // Media navigation
  const nextMedia = () => {
    if (currentMediaIndex < allMedia.length - 1) {
      if (
        allMedia[currentMediaIndex].type === "video" ||
        allMedia[currentMediaIndex + 1].type === "video"
      ) {
        setIsPlaying(false);
        setProgress(0);
        setCurrentTime(0);
        setDuration(0);
        setVideoKey((prev) => prev + 1);
      }
      setCurrentMediaIndex(currentMediaIndex + 1);
    }
  };

  const prevMedia = () => {
    if (currentMediaIndex > 0) {
      if (
        allMedia[currentMediaIndex].type === "video" ||
        allMedia[currentMediaIndex - 1].type === "video"
      ) {
        setIsPlaying(false);
        setProgress(0);
        setCurrentTime(0);
        setDuration(0);
        setVideoKey((prev) => prev + 1);
      }
      setCurrentMediaIndex(currentMediaIndex - 1);
    }
  };

  // Hide controls while playing after a short delay
  useEffect(() => {
    if (!isPlaying) return undefined;

    const timer = setTimeout(() => setShowControls(false), 3000);
    return () => clearTimeout(timer);
  }, [isPlaying, currentMediaIndex, videoKey]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || allMedia[currentMediaIndex]?.type !== "video") return;

    video.playbackRate = playbackRate;
  }, [playbackRate, currentMediaIndex, allMedia]);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      claimActive(video);
      video.play().catch((error) => {
        console.error("Video play failed:", error);
        setIsPlaying(false);
        releaseActive();
      });
    } else {
      video.pause();
      releaseActive();
    }
    setShowControls(true);
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  const handleSeek = (e) => {
    if (!videoRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    videoRef.current.currentTime = pos * videoRef.current.duration;
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  // Document preview handler
  const handleDocumentClick = (url) => {
    window.open(url, "_blank");
  };

  // Handle image click to open full preview
  const handleImageClick = () => {
    setFullPreviewIndex(currentMediaIndex);
    setShowFullPreview(true);
  };

  // Handle video fullscreen button click
  const handleVideoFullscreen = () => {
    setFullPreviewIndex(currentMediaIndex);
    setShowFullPreview(true);
  };

  // Don't render if no media
  if (allMedia.length === 0) {
    return null;
  }

  return (
    <div>
      {/* Combined media display */}
      <div
        className="d-flex flex-column align-items-center justify-content-center"
        style={{ position: "relative" }}
        onMouseEnter={() => setShowControls(true)}
        onMouseLeave={() => {
          if (isPlaying) {
            setTimeout(() => setShowControls(false), 2000);
          }
        }}
      >
        {/* Display current media item */}
        {allMedia[currentMediaIndex].type === "image" ? (
          <div
            style={{
              width: "100%",
              maxHeight: "70vh",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              overflow: "hidden",
              borderRadius: "20px",
              marginBottom: "20px",
              border: "1px solid var(--Bg-Border, #2A4052)",
              cursor: "pointer",
            }}
            onClick={handleImageClick}
          >
            <img
              src={allMedia[currentMediaIndex].url}
              alt="Post"
              style={{
                maxWidth: "90%",
                maxHeight: "60vh",
                width: "auto",
                height: "auto",
                objectFit: "cover",
              }}
            />
          </div>
        ) : allMedia[currentMediaIndex].type === "video" ? (
          <>
            <div
              style={{
                width: "100%",
                maxHeight: "70vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                overflow: "hidden",
                borderRadius: "20px",
                marginBottom: "20px",
                border: "1px solid var(--Bg-Border, #2A4052)",
              }}
            >
              <video
                ref={videoRef}
                key={`video-${currentMediaIndex}-${videoKey}`}
                src={allMedia[currentMediaIndex].url}
                onClick={togglePlay}
                onPlay={handleVideoPlay}
                onPause={handleVideoPause}
                onTimeUpdate={handleVideoTimeUpdate}
                onLoadedMetadata={handleVideoLoadedMetadata}
                onDurationChange={handleVideoLoadedMetadata}
                onEnded={handleVideoEnded}
                muted={isMuted}
                style={{
                  maxWidth: "90%",
                  maxHeight: "60vh",
                  width: "auto",
                  height: "auto",
                  objectFit: "cover",
                  cursor: "pointer",
                }}
                playsInline
              >
                Your browser does not support the video tag.
              </video>
            </div>

            {/* Video controls */}
            {showControls && (
              <div
                style={{
                  position: "absolute",
                  bottom: "20px",
                  left: "0",
                  right: "0",
                  padding: "10px 15px",
                  backgroundColor: "rgba(0, 0, 0, 0.6)",
                  borderBottomLeftRadius: "20px",
                  borderBottomRightRadius: "20px",
                  zIndex: 3,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    marginBottom: "8px",
                  }}
                >
                  <button
                    onClick={togglePlay}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "white",
                      padding: "5px",
                    }}
                  >
                    {isPlaying ? (
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="white"
                        strokeWidth="2"
                      >
                        <rect x="6" y="4" width="4" height="16" />
                        <rect x="14" y="4" width="4" height="16" />
                      </svg>
                    ) : (
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="white"
                        strokeWidth="2"
                      >
                        <polygon points="5 3 19 12 5 21 5 3" />
                      </svg>
                    )}
                  </button>

                  <button
                    onClick={toggleMute}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "white",
                      padding: "5px",
                    }}
                  >
                    {isMuted ? (
                      <img
                        src="/icons/mute.svg"
                        alt="Unmute"
                        style={{ width: "20px", height: "20px" }}
                      />
                    ) : (
                      <img
                        src="/icons/unmute.svg"
                        alt="Mute"
                        style={{ width: "20px", height: "20px" }}
                      />
                    )}
                  </button>

                  <div style={{ position: "relative" }}>
                    <button
                      onClick={() => setShowSpeedMenu((prev) => !prev)}
                      style={{
                        background: "none",
                        border: "1px solid rgba(255,255,255,0.5)",
                        borderRadius: "12px",
                        cursor: "pointer",
                        color: "white",
                        padding: "4px 8px",
                        fontSize: "12px",
                        minWidth: "44px",
                      }}
                    >
                      {playbackRate}x
                    </button>

                    {showSpeedMenu && (
                      <div
                        style={{
                          position: "absolute",
                          bottom: "140%",
                          left: 0,
                          backgroundColor: "rgba(0,0,0,0.9)",
                          borderRadius: "8px",
                          padding: "4px 0",
                          minWidth: "70px",
                        }}
                      >
                        {[0.5, 1, 1.25, 1.5, 2].map((rate) => (
                          <div
                            key={rate}
                            onClick={() => {
                              setPlaybackRate(rate);
                              setShowSpeedMenu(false);
                            }}
                            style={{
                              padding: "4px 10px",
                              fontSize: "12px",
                              color:
                                rate === playbackRate ? "#008CFF" : "#FFFFFF",
                              cursor: "pointer",
                              backgroundColor:
                                rate === playbackRate
                                  ? "rgba(0,140,255,0.2)"
                                  : "transparent",
                            }}
                          >
                            {rate}x
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <span
                    style={{
                      color: "white",
                      fontSize: "12px",
                      minWidth: "40px",
                    }}
                  >
                    {formatTime(currentTime)}
                  </span>
                  <div
                    style={{
                      flex: 1,
                      height: "4px",
                      backgroundColor: "rgba(255,255,255,0.3)",
                      borderRadius: "2px",
                      cursor: "pointer",
                    }}
                    onClick={handleSeek}
                  >
                    <div
                      style={{
                        width: `${progress}%`,
                        height: "100%",
                        backgroundColor: "#008CFF",
                        borderRadius: "2px",
                      }}
                    />
                  </div>
                  <span
                    style={{
                      color: "white",
                      fontSize: "12px",
                      minWidth: "40px",
                    }}
                  >
                    {formatTime(duration)}
                  </span>
                  {/* Fullscreen button - positioned at bottom right of controls */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleVideoFullscreen();
                    }}
                    style={{
                      //   position: "absolute",
                      //   bottom: "10px",
                      //   right: "15px",
                      background: "rgba(0, 0, 0, 0.7)",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                      color: "white",
                      padding: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 10,
                    }}
                    title="Open in fullscreen"
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "rgba(0, 0, 0, 0.9)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "rgba(0, 0, 0, 0.7)";
                    }}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* Play button overlay — hidden while video is playing */}
            {!isPlaying && (
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "60px",
                  height: "60px",
                  backgroundColor: "rgba(0, 0, 0, 0.6)",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  zIndex: 2,
                  pointerEvents: "auto",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  togglePlay();
                }}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polygon points="5 3 19 12 5 21 5 3"></polygon>
                </svg>
              </div>
            )}
          </>
        ) : (
          // Document preview
          <div
            style={{
              width: "100%",
              height: "300px",
              backgroundColor: "#f5f5f5",
              borderRadius: "20px",
              marginBottom: "20px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              position: "relative",
              overflow: "hidden",
            }}
            onClick={() => handleDocumentClick(allMedia[currentMediaIndex].url)}
          >
            <img
              src="/icons/pdf-icon.svg"
              alt="PDF Document"
              style={{
                width: "80px",
                height: "80px",
                marginBottom: "15px",
              }}
            />
            <div
              style={{
                position: "absolute",
                bottom: "0",
                left: "0",
                backgroundColor: "rgba(0, 0, 0, 0.7)",
                color: "white",
                padding: "10px",
                textAlign: "center",
              }}
            >
              Click to view PDF document
            </div>
          </div>
        )}

        {/* Navigation controls */}
        {allMedia.length > 1 && (
          <>
            {/* Previous arrow */}
            {currentMediaIndex > 0 && (
              <div
                style={{
                  position: "absolute",
                  left: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  backgroundColor: "rgba(0,0,0,0.5)",
                  borderRadius: "50%",
                  width: "32px",
                  height: "32px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  zIndex: 1,
                }}
                onClick={prevMedia}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </div>
            )}

            {/* Next arrow */}
            {currentMediaIndex < allMedia.length - 1 && (
              <div
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  backgroundColor: "rgba(0,0,0,0.5)",
                  borderRadius: "50%",
                  width: "32px",
                  height: "32px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  zIndex: 1,
                }}
                onClick={nextMedia}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </div>
            )}

            {/* Media indicators */}
            <div
              style={{
                position: "absolute",
                bottom: "30px",
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                gap: "6px",
              }}
            >
              {allMedia.map((_, index) => (
                <div
                  key={index}
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor:
                      index === currentMediaIndex
                        ? "#008CFF"
                        : "rgba(255,255,255,0.5)",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    setCurrentMediaIndex(index);
                  }}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Full Media Preview Modal */}
      <FullMediaPreview
        isOpen={showFullPreview}
        onClose={() => setShowFullPreview(false)}
        media={allMedia}
        initialIndex={fullPreviewIndex}
      />
    </div>
  );
};

export default RenderMediaGroup;
